<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width: 180px;">
              <label class="filter-label">Hasta</label>
              <b-form-input type="date" size="sm" v-model="filters.date_end" />
            </div>
            <div class="list-view-filter">
              <label class="filter-label">Finalizados</label>
              <b-form-checkbox v-model="filters.include_finished" switch size="sm">
                Agregar finalizados
              </b-form-checkbox>
            </div>
            <b-button variant="outline-dark"
                      size="sm"
                      style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;"
                      v-b-tooltip.hover
                      title="Aplicar filtros"
                      aria-label="Aplicar filtros"
                      @click="filterCalendar()">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-card>
        <b-table v-if="table.items.length || table.isBusy"
                 class="app-table"
                 responsive
                 :stacked="isMobileViewport ? 'md' : false"
                 sticky-header="60vh"
                 hover
                 small
                 :items="calendarItems"
                 :fields="table.fields"
                 :current-page="table.currentPage"
                 :per-page="table.perPage"
                 :busy="table.isBusy"
                 :tbody-tr-class="rowClass"
                 @row-clicked="onRowClicked">
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle" />
              <strong>Cargando...</strong>
            </div>
          </template>

          <template #cell(period)="data">
            <div>
              <strong>{{ moment(data.item.date_start).format('DD MMMM') }}</strong>
              <span class="text-muted"> al </span>
              <strong>{{ moment(data.item.date_end).format('DD MMMM') }}</strong>
            </div>
          </template>

          <template #cell(assigned)="data">
            <div>{{ calendarAssignment(data.item) }}</div>
          </template>

          <template #cell(observations)="data">
            <div v-if="data.item.observation"
                 class="text-truncate"
                 v-b-tooltip.hover
                 :title="data.item.observation">
              {{ data.item.observation }}
            </div>
          </template>

          <template #cell(status)="data">
            <b-badge :variant="calendarStatus(data.item).variant">{{ calendarStatus(data.item).label }}</b-badge>
          </template>
        </b-table>

        <EmptyState v-else
                    icon="calendar_month"
                    title="No se encontraron períodos"
                    description="Ajustá la fecha de finalización para ampliar la búsqueda." />

        <div class="app-table-footer">
          <span class="list-view-toolbar__count">{{ calendarItems.length }} registros</span>
        </div>
      </b-card>

      <b-card body-class="py-2">
        <section class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="arrow_back" class="mr-1" /> Volver
          </b-button>
          <b-pagination class="mb-0"
                        size="sm"
                        pills
                        :total-rows="calendarItems.length"
                        :per-page="table.perPage"
                        v-model="table.currentPage" />
        </section>
      </b-card>

      <b-sidebar id="sidebar-seller-calendar-detail"
                 v-model="detailDrawer"
                 right
                 shadow
                 backdrop
                 title="Detalle del período"
                 width="420px"
                 @hidden="hideDetail">
        <div class="list-view-drawer" v-if="itemSelected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code">{{ calendarPeriod(itemSelected) }}</span>
              <div>{{ calendarAssignment(itemSelected) }}</div>
              <div class="list-view-drawer__meta">{{ calendarStatus(itemSelected).label }}</div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm" variant="outline-secondary" :disabled="!canGoPrev" v-b-tooltip.hover title="Período anterior" @click="goPrev()">
                <AppIcon name="chevron-up" />
              </b-button>
              <b-button size="sm" variant="outline-secondary" :disabled="!canGoNext" v-b-tooltip.hover title="Período siguiente" @click="goNext()">
                <AppIcon name="chevron-down" />
              </b-button>
            </div>
          </div>

          <div class="list-view-drawer__stats">
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Desde</span>
              <span class="list-view-drawer__stat-value">{{ moment(itemSelected.date_start).format('DD/MM/YYYY') }}</span>
            </div>
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Hasta</span>
              <span class="list-view-drawer__stat-value">{{ moment(itemSelected.date_end).format('DD/MM/YYYY') }}</span>
            </div>
          </div>

          <section>
            <h3 class="list-view-drawer__section-title"><AppIcon name="assignment" /> Asignación</h3>
            <p class="mb-0">{{ calendarAssignment(itemSelected) }}</p>
          </section>

          <section v-if="itemSelected.observation">
            <h3 class="list-view-drawer__section-title"><AppIcon name="chat" /> Observaciones</h3>
            <p class="mb-0">{{ itemSelected.observation }}</p>
          </section>

          <section>
            <h3 class="list-view-drawer__section-title"><AppIcon name="business" /> Clientes</h3>
            <b-table v-if="tableSub.items.length"
                     class="app-table app-table--compact seller-calendar-customers-table"
                     stacked
                     :items="tableSub.items"
                     :fields="tableSub.fields">
              <template #cell(code)="row"><strong>{{ row.item.customer.code }}</strong></template>
              <template #cell(customers_id)="row">
                <div class="d-flex align-items-center">
                  <b-avatar v-if="row.item.customer.image" :src="row.item.customer.image" size="28px" />
                  <b-avatar v-else :text="customerInitials(row.item.customer.name)" size="28px" />
                  <strong class="ml-2">{{ row.item.customer.name }}</strong>
                  <AppIcon v-if="!row.item.customer.active" name="circle-fill" class="ml-2 text-danger" v-b-tooltip.hover title="Cliente inactivo" />
                </div>
              </template>
              <template #cell(last_news)="row">{{ customerReportLabel(row.item, 'news') }}</template>
              <template #cell(last_payment)="row">{{ customerReportLabel(row.item, 'payments') }}</template>
              <template #cell(last_visits)="row">{{ customerReportLabel(row.item, 'visits') }}</template>
              <template #cell(last_services)="row">{{ customerReportLabel(row.item, 'services') }}</template>
              <template #cell(last_marketing)="row">{{ customerReportLabel(row.item, 'marketing') }}</template>
              <template #cell(last_audit)="row">{{ customerReportLabel(row.item, 'audit') }}</template>
              <template #cell(last_order)="row">{{ customerOrderLabel(row.item) }}</template>
              <template #cell(f_action)="row">
                <b-dropdown right no-caret size="sm" variant="link" boundary="window" toggle-class="seller-calendar-customer-actions-toggle">
                  <template #button-content><AppIcon name="more_vert" label="Opciones" class="mr-1" /><span>Opciones</span></template>
                  <b-dropdown-item @click="goDetailCustomer(row.item)"><AppIcon name="visibility" /> Detalle cliente</b-dropdown-item>
                  <b-dropdown-header>Pedidos</b-dropdown-header>
                  <b-dropdown-item @click="addOrder(row.item)"><AppIcon name="add_shopping_cart" /> Agregar pedido</b-dropdown-item>
                  <b-dropdown-header>Reportes</b-dropdown-header>
                  <b-dropdown-item v-if="accessExtras.INFORME_VISITAS.status" @click="goReportVisit(row.item)"><AppIcon name="location_on" /> Reporte de visitas</b-dropdown-item>
                  <b-dropdown-item v-if="accessExtras.INFORME_COBRANZAS.status" @click="goReportPayment(row.item)"><AppIcon name="payments" /> Reporte de cobranzas</b-dropdown-item>
                  <b-dropdown-item v-if="accessExtras.INFORME_NOVEDADES.status" @click="goReportNews(row.item)"><AppIcon name="chat" /> Reporte de novedades</b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
            <EmptyState v-else icon="business" title="Sin clientes vinculados" />
          </section>
        </div>
      </b-sidebar>
    </b-col>
  </b-row>
</template>

<script>
import serviceAPI from './services'
import Error from '@/handler/error'
import Helper from '@/handler/helper'
import StorageLocal from '@/handler/storageLocal'
import Profiles from '@/config/profiles'
import Modules from '@/config/modules'
import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
import moment from 'moment'

export default {
  components: { TabHeaderActions },
  data: () => ({
    access: {
      module_id: Modules.VENDEDORES,
      profile_id: Profiles.PERSONAL,
      view_reference: 'listCalendarSellers',
      elements: {},
    },
    accessExtras: {
      INFORME_VISITAS: { module: Modules.INFORME_VISITAS, status: false },
      INFORME_COBRANZAS: { module: Modules.INFORME_COBRANZAS, status: false },
      INFORME_NOVEDADES: { module: Modules.INFORME_NOVEDADES, status: false },
      INFORME_SERVICIOS: { module: Modules.INFORME_SERVICIOS, status: false },
      INFORME_MARKETING: { module: Modules.INFORME_MARKETING, status: false },
      INFORME_AUDITORIA: { module: Modules.INFORME_AUDITORIA, status: false },
    },
    filtersOpen: false,
    isMobileViewport: false,
    mobileMql: null,
    detailDrawer: false,
    itemSelected: null,
    table: {
      items: [],
      fields: [],
      currentPage: 1,
      perPage: 50,
      isBusy: false,
    },
    tableSub: { items: [], fields: [] },
    arr: { customers: [] },
    filters: {
      date_end: moment().add(7, 'd').format('YYYY-MM-DD'),
      include_finished: false,
    },
  }),
  computed: {
    calendarItems() {
      return this.table.items
        .filter(item => this.filters.include_finished || this.calendarStatus(item).key !== 'finished')
        .slice()
        .sort((a, b) => {
          var byStart = (a.date_start || '').localeCompare(b.date_start || '')
          if (byStart !== 0) return byStart
          var byEnd = (a.date_end || '').localeCompare(b.date_end || '')
          if (byEnd !== 0) return byEnd
          return (a.id || 0) - (b.id || 0)
        })
    },
    currentIndex() {
      return this.itemSelected ? this.calendarItems.findIndex(item => item.id === this.itemSelected.id) : -1
    },
    canGoPrev() {
      return this.currentIndex > 0 && this.calendarStatus(this.calendarItems[this.currentIndex - 1]).key !== 'finished'
    },
    canGoNext() {
      return this.currentIndex >= 0 && this.currentIndex < this.calendarItems.length - 1 && this.calendarStatus(this.calendarItems[this.currentIndex + 1]).key !== 'finished'
    },
  },
  watch: {
    isMobileViewport() {
      this.loadCalendarFields()
    },
    filtersOpen(value) {
      StorageLocal.setValue('filters_open_list_calendar_sellers', value ? 'true' : 'false')
    },
  },
  created() {
    Helper.hasAccessView(this.access)
    Object.keys(this.accessExtras).forEach(key => {
      this.accessExtras[key].status = Helper.moduleEnabled(this.accessExtras[key].module)
    })
    this.loadCalendarFields()
    this.loadCustomerFields()
  },
  mounted() {
    this.filtersOpen = StorageLocal.getValue('filters_open_list_calendar_sellers') === 'true'
    this.mobileMql = window.matchMedia('(max-width: 767.98px)')
    this.isMobileViewport = this.mobileMql.matches
    this.mobileMql.addEventListener('change', this.updateMobileViewport)
    this.filterCalendar()
  },
  beforeDestroy() {
    if (this.mobileMql) this.mobileMql.removeEventListener('change', this.updateMobileViewport)
  },
  methods: {
    moment,
    updateMobileViewport(event) {
      this.isMobileViewport = event.matches
    },
    alignClass(desktopClass) {
      return this.isMobileViewport ? 'text-left' : desktopClass
    },
    loadCalendarFields() {
      this.table.fields = [
        { key: 'period', label: 'Período', class: 'align-middle ' + this.alignClass('text-left') },
        { key: 'assigned', label: 'Asignado', class: 'align-middle ' + this.alignClass('text-left') },
        { key: 'observations', label: 'Observaciones', class: 'align-middle ' + this.alignClass('text-left') },
        { key: 'status', label: 'Estado', class: 'align-middle ' + this.alignClass('text-center') },
      ]
    },
    loadCustomerFields() {
      this.tableSub.fields = [
        { key: 'code', label: 'Código' },
        { key: 'customers_id', label: 'Cliente' },
      ]
      if (this.accessExtras.INFORME_VISITAS.status) this.tableSub.fields.push({ key: 'last_visits', label: 'Última visita' })
      if (this.accessExtras.INFORME_COBRANZAS.status) this.tableSub.fields.push({ key: 'last_payment', label: 'Última cobranza' })
      if (this.accessExtras.INFORME_NOVEDADES.status) this.tableSub.fields.push({ key: 'last_news', label: 'Última novedad' })
      if (this.accessExtras.INFORME_SERVICIOS.status) this.tableSub.fields.push({ key: 'last_services', label: 'Último servicio' })
      if (this.accessExtras.INFORME_MARKETING.status) this.tableSub.fields.push({ key: 'last_marketing', label: 'Último marketing' })
      if (this.accessExtras.INFORME_AUDITORIA.status) this.tableSub.fields.push({ key: 'last_audit', label: 'Última auditoría' })
      this.tableSub.fields.push({ key: 'last_order', label: 'Último pedido' })
      this.tableSub.fields.push({ key: 'f_action', label: 'Acciones' })
    },
    calendarPeriod(item) {
      return moment(item.date_start).format('DD/MM/YYYY') + ' al ' + moment(item.date_end).format('DD/MM/YYYY')
    },
    calendarAssignment(item) {
      return item.activity ? 'Actividad: ' + item.activity : 'Zona: ' + ((item.zone && item.zone.name) || 'Sin zona')
    },
    calendarStatus(item) {
      var today = moment().format('YYYY-MM-DD')
      if (item.date_start <= today && item.date_end >= today) return { key: 'active', label: 'Activo', variant: 'success' }
      if (item.date_end < today) return { key: 'finished', label: 'Finalizado', variant: 'danger' }
      return { key: 'upcoming', label: 'Próximo', variant: 'warning' }
    },
    customerInitials(name) {
      return (name || '').split(' ').filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join('').toUpperCase()
    },
    onRowClicked(item, index, event) {
      var target = event && event.target
      if (target && target.closest && target.closest('button, a, input, select, textarea, .dropdown')) return
      if (this.calendarStatus(item).key === 'finished') return
      this.openDetail(item)
    },
    rowClass(item, type) {
      if (type !== 'row' || !item) return ''
      var classes = ['seller-calendar-row--' + this.calendarStatus(item).key]
      if (this.detailDrawer && this.itemSelected && item.id === this.itemSelected.id) classes.push('app-table-row-active')
      return classes.join(' ')
    },
    openDetail(item) {
      if (this.calendarStatus(item).key === 'finished') return
      this.itemSelected = item
      this.tableSub.items = item.zone && item.zone.customers ? item.zone.customers : []
      if (item.zone_id) this.loadCustomersZone(item.zone_id)
      this.detailDrawer = true
    },
    hideDetail() {
      this.detailDrawer = false
      this.itemSelected = null
      this.tableSub.items = []
    },
    goPrev() {
      if (this.canGoPrev) this.openDetail(this.calendarItems[this.currentIndex - 1])
    },
    goNext() {
      if (this.canGoNext) this.openDetail(this.calendarItems[this.currentIndex + 1])
    },
    filterCalendar() {
      this.table.isBusy = true
      serviceAPI.filtrarVendedorCalendario({ sellers_id: Helper.getSeller().id, date_end: this.filters.date_end })
        .then((response) => {
          this.table.items = response.data || []
          if (this.itemSelected) {
            var selected = this.table.items.find(item => item.id === this.itemSelected.id)
            if (selected && this.calendarStatus(selected).key !== 'finished') this.openDetail(selected)
            else this.hideDetail()
          }
          this.table.isBusy = false
        })
        .catch((error) => {
          this.table.isBusy = false
          this.$awn.alert(Error.showError(error))
        })
    },
    loadCustomersZone(zoneId) {
      serviceAPI.mostrarClienteZona(zoneId)
        .then((response) => { this.arr.customers = response.data || [] })
        .catch((error) => { this.$awn.alert(Error.showError(error)) })
    },
    customerReportLabel(item, type) {
      var customerCalendar = this.arr.customers.find(element => element.customers_id === item.customers_id)
      var report = customerCalendar && customerCalendar.customer && customerCalendar.customer['last-reports-' + type]
      return report ? moment.duration(moment().diff(moment(report.date), 'days'), 'days').humanize() : 'Sin cargar'
    },
    customerOrderLabel(item) {
      var customerCalendar = this.arr.customers.find(element => element.customers_id === item.customers_id)
      var order = customerCalendar && customerCalendar.customer && customerCalendar.customer['last-order']
      return order ? moment(order.date).format('DD/MM/YYYY') : 'Sin pedidos'
    },
    addOrder(item) {
      this.$router.push({ name: 'OrdersStaffCrudOrdersSellers', query: { customer_id: item.customers_id } })
    },
    goReportVisit(item) {
      this.$router.push({ name: 'ReportsVisitsStaffCrudReportSellerByCustomers', params: { customersID: item.customers_id } })
    },
    goReportPayment(item) {
      this.$router.push({ name: 'ReportsPaymentStaffCrudReportSellerByCustomers', params: { customersID: item.customers_id + '|' + item.customer.code } })
    },
    goReportNews(item) {
      this.$router.push({ name: 'ReportsNewsStaffCrudReportSellerByCustomers', params: { customersID: item.customers_id } })
    },
    goDetailCustomer(item) {
      this.$router.push({ name: 'CustomerViewStaff', params: { customersID: item.customers_id } })
    },
  },
}
</script>

<style>
.seller-calendar-row--active > td {
  background: var(--app-color-success-soft) !important;
}
.seller-calendar-row--active > td:first-child,
.seller-calendar-row--upcoming > td:first-child,
.seller-calendar-row--finished > td:first-child {
  padding-left: 12px !important;
}
.seller-calendar-row--active > td:first-child {
  box-shadow: inset 4px 0 0 var(--app-color-success);
}
.seller-calendar-row--upcoming > td {
  background: var(--app-color-warning-soft) !important;
}
.seller-calendar-row--upcoming > td:first-child {
  box-shadow: inset 4px 0 0 var(--app-color-warning);
}
.seller-calendar-row--finished {
  cursor: not-allowed;
}
.seller-calendar-row--finished > td {
  background: var(--app-color-danger-soft) !important;
}
.seller-calendar-row--finished > td:first-child {
  box-shadow: inset 4px 0 0 var(--app-color-danger);
}
.seller-calendar-customers-table.b-table-stacked > tbody > tr > [data-label]::before {
  width: 132px !important;
  flex: 0 0 132px;
  white-space: normal;
}
.seller-calendar-customers-table.b-table-stacked > tbody > tr + tr > td:first-child {
  padding-top: 14px !important;
  border-top: 2px solid var(--app-color-border-strong) !important;
}
.btn.seller-calendar-customer-actions-toggle {
  display: inline-flex !important;
  align-items: center;
  min-height: 28px;
  padding: 2px 6px !important;
  border: 0 !important;
  border-radius: 6px;
  background: transparent !important;
  color: var(--app-color-text-secondary) !important;
  font-size: var(--app-text-xs);
}
.btn.seller-calendar-customer-actions-toggle:hover,
.btn.seller-calendar-customer-actions-toggle:focus {
  background: var(--app-color-primary-soft) !important;
  color: var(--app-color-primary) !important;
}
</style>
